import {Component, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {
    BucketName,
    DocumentAttachment,
    FileAttachment,
    OrganizationApiService,
    OrganizationDocumentResponse,
    OrganizationResponse,
} from '@npck/af-frontend-api';
import {LocalizationService, NESTED_TAB, RouterService, rxSpinner, Spinner} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {concat, defaultIfEmpty, last, Observable, of, zip} from 'rxjs';
import {AttachmentForm} from '../../../../components/files/file/file.component';
import {ORGANIZATION_MIME_TYPES} from '../../../../domain/constants';
import {ORGANIZATION_DOCUMENT_ID, ORGANIZATION_ID} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';
import {AdminPanelTab} from '../../admin.component';
import {ORGANIZATION_SCOPE} from '../create-edit/organization-create-edit.component';
import {OrganizationTab} from '../details/organization-details.component';

const DOCUMENTS_TAB: OrganizationTab = 'tabOrganizationDocuments';

@Component({
    templateUrl: 'organization-document-create-edit.component.html',
    standalone: false,
})
export class OrganizationDocumentCreateEditComponent implements OnInit {
    Spinner = Spinner;
    Path = Path;
    BucketName = BucketName;
    ORGANIZATION_MIME_TYPES = ORGANIZATION_MIME_TYPES;
    organizationScope = [ORGANIZATION_SCOPE];
    organizationId: string;
    documentId: string;
    document?: OrganizationDocumentResponse;
    organization?: OrganizationResponse;
    attachments: DocumentAttachment[] = [];
    isLoaded = false;
    form: FormGroup<OrganizationDocumentsForm>;
    tabPathToTab: Record<string, AdminPanelTab> = {[Path.ORGANIZATIONS]: 'tabOrganizations'};

    constructor(
        private formBuilder: FormBuilder,
        private organizationApiService: OrganizationApiService,
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        private snackbarService: SnackbarService,
        private localizationService: LocalizationService,
    ) {
        this.organizationId = this.activatedRoute.snapshot.paramMap.get(ORGANIZATION_ID);
        this.documentId = this.activatedRoute.snapshot.paramMap.get(ORGANIZATION_DOCUMENT_ID);
        this.form = new FormGroup<OrganizationDocumentsForm>({
            attachments: this.formBuilder.array([] as FormGroup<AttachmentForm>[]),
        });
    }

    ngOnInit(): void {
        Spinner.show('initial');
        zip([
            this.organizationApiService.getOrganization(this.organizationId),
            this.documentId
                ? this.organizationApiService.getOrganizationDocument(this.organizationId, this.documentId)
                : of(null),
        ])
            .pipe(rxSpinner('initial'))
            .subscribe(([organization, document]) => {
                this.organization = organization;
                this.document = document;
                this.attachments = document ? [this.toAttachment(document)] : [];
                this.isLoaded = true;
            });
    }

    breadcrumbCustomizer = (path: string): string => {
        if (path === this.organizationId) {
            return this.organization?.nameRu;
        }
        if (path === this.documentId) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#OrganizationDocumentEdit');
        }
        return path;
    };

    navigateBack(): void {
        this.routerService.navigateToTab<AdminPanelTab>(
            `${Path.ADMIN_PANEL_ORGANIZATIONS}/${this.organizationId}`,
            'tabOrganizations',
            {queryParams: {[NESTED_TAB]: DOCUMENTS_TAB}});
    }

    submit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        this.saveDocuments()
            .pipe(rxSpinner('submit'))
            .subscribe(() => {
                this.navigateBack();
                this.snackbarService.showSnackBar('i18n_af_snackbarMessageOrganizationDocumentSaved', 'success', false);
            });
    }

    private toAttachment(document: OrganizationDocumentResponse): DocumentAttachment {
        return <DocumentAttachment>{
            docType: document.type,
            files: [<FileAttachment>{id: document.fileId, name: document.fileName}],
        };
    }

    private saveDocuments(): Observable<unknown> {
        const attachments = (this.form.controls.attachments?.getRawValue() || [])
            .filter(attachment => !!attachment.fileType && !!attachment.files?.length);
        const requests = attachments.flatMap(attachment => (attachment.files || [])
            .filter(file => !file.isDeleted && !!file.id)
            .map(file => this.documentId
                ? this.organizationApiService.updateOrganizationDocument(this.organizationId, this.documentId, {
                    type: attachment.fileType,
                    fileId: file.id,
                })
                : this.organizationApiService.createOrganizationDocument(this.organizationId, {
                    type: attachment.fileType,
                    fileId: file.id,
                })));
        return concat(...requests).pipe(defaultIfEmpty(null), last());
    }
}

export interface OrganizationDocumentsForm {
    attachments?: FormArray<FormGroup<AttachmentForm>>;
}
